import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { HoDDoSAssessmentContent } from './HoDDoSAssessmentContent';
import { PAGE_TEXT } from '../../../lang';
import { ApplicantParticulars } from '../../../DRCAssessment';
import { AssessmentSidebar, ASSESSMENT_MODULE } from '../../../components';
import { getApplicationStatus } from '../../../../Nomination/graphql';
import { type SidebarData, Button, Alert } from '../../../../../components';
import { APPLICATION_STATUS_TYPE } from '../../../../../utils/statusConstants/statusConstants';

export const HoDDoSAssessmentDetail: React.FunctionComponent = () => {
  const { applicationId = '' } = useParams();
  const navigate = useNavigate();

  const {
    applicationStatus,
    loading: applicationStatusLoading,
    error: applicationStatusError,
  } = getApplicationStatus(applicationId);

  const [activatedModuleId, setActivatedModuleId] = useState(
    ASSESSMENT_MODULE.HOD_DOS_ASSESSMENT,
  );

  /* if the application is not being assessed by hod/dos, redirect to monitor */
  if (
    !applicationStatusLoading &&
    applicationStatus !== APPLICATION_STATUS_TYPE.UNDER_HOD_DOS
  ) {
    navigate(`/internal/monitor/${applicationId}`);
  }

  const handleChangeActivated = (activatedId: string): void => {
    setActivatedModuleId(activatedId as ASSESSMENT_MODULE);
  };

  const sidebarData: SidebarData[] = [
    {
      id: ASSESSMENT_MODULE.PARTICULARS,
      name: PAGE_TEXT.DRC_ASSESSMENT_DETAILS.PARTICULARS.TITLE,
    },
    {
      id: ASSESSMENT_MODULE.HOD_DOS_ASSESSMENT,
      name: PAGE_TEXT.HOD_DOS_ASSESSMENT.ASSESSMENT.TITLE,
    },
  ];

  const assessmentModulesMap: Partial<
    Record<ASSESSMENT_MODULE, { component: any; moduleTitle: string }>
  > = {
    [ASSESSMENT_MODULE.PARTICULARS]: {
      component: <ApplicantParticulars showGoDetail />,
      moduleTitle: PAGE_TEXT.DRC_ASSESSMENT_DETAILS.PARTICULARS.TITLE,
    },
    [ASSESSMENT_MODULE.HOD_DOS_ASSESSMENT]: {
      component: <HoDDoSAssessmentContent />,
      moduleTitle: PAGE_TEXT.HOD_DOS_ASSESSMENT.ASSESSMENT.TITLE,
    },
  };

  const activatedModule:
    | { component: React.ReactNode; moduleTitle: string }
    | undefined = assessmentModulesMap[activatedModuleId];

  const moduleComponent = activatedModule?.component ?? activatedModuleId;
  const moduleTitle = activatedModule?.moduleTitle ?? activatedModuleId;

  const handleBack = (): void => {
    window.location.href = '/internal/hod-dos-homepage/hoddosassessments';
  };

  if (applicationStatusError) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent:
            `Error Occurs.` + (applicationStatusError as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  return (
    <>
      <PageContainer>
        <div className="sidebar-area">
          <AssessmentSidebar
            onChangeActivated={handleChangeActivated}
            collapsible={true}
            sidebarData={sidebarData}
            drawerWidth={232}
            currentActivatedModule={activatedModuleId}
            drawerInnerWidth="14rem"
            buttonField={
              <div className="flex flex-col gap-6">
                <Button variant="outlined" onClick={handleBack}>
                  {PAGE_TEXT.BACK}
                </Button>
              </div>
            }
          />
        </div>
        <Container>
          <h1>{moduleTitle}</h1>
          <div className="form-area">{moduleComponent}</div>
        </Container>
      </PageContainer>
    </>
  );
};

const PageContainer = styled.div`
  display: flex;
  flex-direction: row;

  .sidebar-area {
    width: min-content;
  }

  h1 {
    margin-top: 2rem;
    font-size: 1.5rem;
    font-weight: 700;
    text-align: center;
    color: #951d1d;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;

  .form-area {
    flex: 0.95;
    margin: 1rem;
  }
`;
